import { useContext, useEffect, useState } from "react";
import { useFormik } from "formik";
import { StepperContext, useLoanApplication } from "../../contexts";
import {
  CardList,
  Dropzone,
  InputField,
  StepNextButton,
  StepPrevButton,
} from "../../components";
import {
  UploadInvoiceFields,
  LOAN_UPLOAD_INVOICE_INITIAL_VALUES,
  LOAN_UPLOAD_INVOICE_VALIDATION_SCHEMA,
} from "./service";
import { ArrowLeftIcon, DocumentIcon, SuccessIcon } from "../../assets/icons";
import { CardVariantType } from "../../components/CardList/CardList";
import { useLoanMetrics } from "../../hooks";
import { useDpConfig } from "../../contexts/dpConfig";

const DUMMY_INVOICE_NAME = "dummy-invoice.pdf";

const LoanUploadInvoice = () => {
  const [uploading, setUploading] = useState(false);
  const [repayableByDP, setRepayableByDP] = useState(false);
  const [
    invoiceDiscountingRepayableByDPFlag,
    setInvoiceDiscountingRepayableByDPFlag,
  ] = useState(false);
  const [
    payableFinancingRepayableByDPFlag,
    setPayableFinancingRepayableByDPFlag,
  ] = useState(false);

  const {
    state: { invoices, sourceId },
    actions: { update },
  } = useLoanApplication();

  const {
    state: { settings },
  } = useDpConfig();

  const { nextStep, gotoStep, previousStep } = useContext(StepperContext);

  const { invoiceFinanceProduct, payableFinancingProduct } = useLoanMetrics();

  useEffect(() => {
    setInvoiceDiscountingRepayableByDPFlag(
      invoiceFinanceProduct?.flags?.repayableByDP
    );
    setPayableFinancingRepayableByDPFlag(
      payableFinancingProduct?.flags?.repayableByDP
    );
  }, [invoiceFinanceProduct, payableFinancingProduct]);

  useEffect(() => {
    setRepayableByDP(
      invoiceDiscountingRepayableByDPFlag || payableFinancingRepayableByDPFlag
    );
  }, [invoiceDiscountingRepayableByDPFlag, payableFinancingRepayableByDPFlag]);

  useEffect(() => {
    if (settings.disableInvoiceUpload) {
      update(
        {
          invoiceFiles: [new File([], DUMMY_INVOICE_NAME)],
        },
        { local: true }
      );
      nextStep();
      nextStep();
      nextStep();
    }
  }, [settings.disableInvoiceUpload]);

  const { errors, touched, values, handleChange, handleSubmit, setFieldValue } =
    useFormik<UploadInvoiceFields>({
      initialValues: {
        ...LOAN_UPLOAD_INVOICE_INITIAL_VALUES,
        invoices: invoices.map(
          (invoice) => new File([], invoice.invoiceId + ".pdf")
        ),
      },
      validationSchema: LOAN_UPLOAD_INVOICE_VALIDATION_SCHEMA,
      onSubmit: async (values) => {
        try {
          await update(
            {
              invoiceFiles: values.invoices,
            },
            { local: true }
          );
          if (sourceId) gotoStep([1, 1]);
          else {
            nextStep();
            nextStep();
            nextStep();
          }
        } catch (err) {
          console.log(err);
        }
      },
    });

  const handleUpload = (file: File) => {
    setUploading(true);
    setFieldValue("invoices", [file]);
    setUploading(false);
  };

  return (
    <div className="cx-grow cx-flex cx-flex-col cx-items-center cx-justify-center cx-w-full cx-p-8 cx-max-w-[960px]">
      <div className="cx-flex cx-flex-col cx-justify-center cx-items-center cx-gap-2">
        <div className="cx-text-3xl cx-font-bold cx-text-center">
          Upload invoice
        </div>
        <div className="cx-text-text-secondary cx-text-center">
          Please note the invoicing party must be the same as the entity
          applying for the loan, and all invoices must be in AED.
        </div>
      </div>
      <div className="cx-w-full cx-max-w-[400px]">
        <form onSubmit={handleSubmit}>
          <div className="cx-w-full cx-bg-background-default cx-my-10 cx-rounded-lg">
            <div className="cx-mb-4">
              <InputField
                label="Company name"
                inputProps={{
                  name: "companyName",
                  value: values.companyName,
                  onChange: handleChange,
                }}
                error={touched.companyName ? errors.companyName : ""}
              />
            </div>
            {values.invoices.map((invoice, index) => (
              <CardList
                key={index}
                variant={
                  uploading
                    ? CardVariantType.UPLOADING
                    : invoice.name
                    ? CardVariantType.UPLOADED
                    : CardVariantType.UPLOAD
                }
                title="First invoice"
                description={invoice.name}
                inputProps={{ name: "invoice" }}
                icon={
                  uploading ? (
                    <DocumentIcon width="100%" height="100%" />
                  ) : (
                    <SuccessIcon
                      width="100%"
                      height="100%"
                      className="cx-text-background-success"
                    />
                  )
                }
                onChange={async (file) => {
                  if (!file) return;
                  handleUpload(file);
                }}
                error={touched.invoices?.[index] && !!errors.invoices?.[index]}
                truncateDescriptionLength={35}
              />
            ))}
            {!values.invoices?.length && (
              <Dropzone
                variant={uploading ? "uploading" : "simple"}
                extensions={["PDF only", "Maximum file size is 5 MB"]}
                options={{
                  accept: { "application/pdf": [".pdf"] },
                  maxFiles: 1,
                  maxSize: 100 * 1024 * 1024, // 100MB to byte
                }}
                onDrop={async (e) => {
                  const file = e[0];
                  if (!file) return;
                  handleUpload(file);
                }}
              />
            )}
            {!repayableByDP && (
              <div className="cx-mt-4 cx-text-text-secondary cx-font-normal cx-text-center">
                If your application is successful we will provide an offer for a
                portion of the invoiced amount.
              </div>
            )}
          </div>
          {/* <Button fullWidth label="Next" type="submit" /> */}
          <div className="cx-w-full cx-flex cx-flex-row cx-gap-x-4">
            <div className="cx-w-1/4">
              <StepPrevButton
                label={<ArrowLeftIcon />}
                type="button"
                onClick={() => {
                  previousStep();
                }}
              />
            </div>
            <div className="cx-w-3/4">
              <StepNextButton
                type="button"
                label="Next"
                disabled={uploading || !values.invoices?.length}
                onClick={handleSubmit}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoanUploadInvoice;
